import { useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { FiCopy } from "react-icons/fi";
import { parseISO } from "date-fns";

interface UserTableViewProps {
  tableData: any[];
  isInitialLoad: boolean;
  isLoading: boolean;
  isMobile: boolean;
  t: TFunction;
}

export default function UserTableView({
  tableData,
  isInitialLoad,
  isLoading,
  isMobile,
  t,
}: UserTableViewProps) {
  const { i18n } = useTranslation();

  // Row styling
  const getRowStyle = useCallback((params: any) => {
    const weekNumber = params.data.weekNumber;
    const colors = [
      { background: "#f0f9ff" }, // light blue
      { background: "#fef3c7" }, // light yellow
      { background: "#f0fdf4" }, // light green
      { background: "#fce7f3" }, // light pink
      { background: "#f5f3ff" }, // light purple
    ];
    return colors[weekNumber % colors.length];
  }, []);

  // Column definitions
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: t("userProfile.week"),
        field: "weekNumber",
        filter: true,
        sortable: true,
        flex: 1,
        hide: isMobile,
      },
      {
        headerName: t("userProfile.month"),
        field: "month",
        filter: true,
        sortable: true,
        flex: 1,
        hide: isMobile,
      },
      {
        headerName: !isMobile ? t("userProfile.day") : "",
        field: "dayOfWeek",
        filter: true,
        sortable: true,
        ...(isMobile ? { width: 38 } : { flex: 1 }),
        cellRenderer: (params: any) => {
          const displayDate = isMobile
            ? params.value.substring(0, 3)
            : params.value;
          return <span>{displayDate}</span>;
        },
      },
      {
        headerName: t("userProfile.date"),
        field: "date",
        filter: true,
        sortable: true,
        ...(isMobile ? { width: 70 } : { flex: 1 }),
        cellRenderer: (params: any) => {
          const displayDate = isMobile
            ? (() => {
                const date = parseISO(params.value);
                const locale = i18n.language === "es" ? "es-ES" : "en-US";
                return new Date(date).toLocaleDateString(locale, {
                  // year: "2-digit",
                  month: "2-digit",
                  day: "2-digit",
                });
              })()
            : params.value;
          return (
            <span>
              {displayDate}
              {params.data.isHoliday && <span className="tw-ml-1">🎉</span>}
              {params.data.type === "pto" && (
                <span className="tw-ml-1">🏖️</span>
              )}
            </span>
          );
        },
      },
      {
        headerName: t("userProfile.hours"),
        field: "hours",
        filter: true,
        sortable: true,
        flex: 1,
        cellRenderer: (params: any) => {
          const hours = params.value || 0;
          const flatAmount = params.data.flatAmount;
          const giftedHours = params.data.giftedHours;
          const isHoliday = params.data.isHoliday;
          const type = params.data.type;
          const ptoStatus = params.data.ptoStatus;
          const ptoType = params.data.ptoType;

          return (
            <div className="tw-flex tw-flex-col">
              {type === "pto" ? (
                <span
                  className={`${
                    ptoStatus === "Approved" ||
                    ptoStatus === "Balance_Overridden"
                      ? "tw-text-green-600"
                      : ptoStatus === "Pending"
                      ? "tw-text-orange-500"
                      : "tw-text-red-600"
                  }`}
                >
                  {hours.toFixed(2)} hrs - PTO ({ptoType})
                  {!isMobile && ` - ${ptoStatus}`}
                </span>
              ) : (
                <>
                  {hours > 0 && (
                    <span>
                      {hours.toFixed(2)} {t("userProfile.hrs")}
                    </span>
                  )}
                  {isHoliday && flatAmount > 0 && (
                    <span className="tw-text-xs tw-text-yellow-600">
                      ${flatAmount.toFixed(2)}{" - Holiday Pay "}
                    </span>
                  )}
                  {isHoliday && giftedHours > 0 && (
                    <span className="tw-text-xs tw-text-green-600">
                      {giftedHours.toFixed(2)} hrs
                      {" - Holiday Pay "}
                    </span>
                  )}
                </>
              )}
            </div>
          );
        },
      },
    ],
    [isMobile, t, i18n.language]
  );

  // Context menu hook
  const {
    handleCellContextMenu,
    handleCopyFromContextMenu,
    ContextMenuComponent,
  } = AgGridContextMenu({
    menuItems: [
      {
        label: "Copy Cell Value",
        icon: FiCopy,
        onClick: () => handleCopyFromContextMenu(),
        disabled: (value: string) => !value.length,
      },
    ],
  });

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  // Group data by week number
  const weeklyData = useMemo(() => {
    const grouped: { [key: number]: any[] } = {};

    tableData.forEach((row) => {
      const week = row.weekNumber;
      if (!grouped[week]) {
        grouped[week] = [];
      }
      grouped[week].push(row);
    });
    // Convert to array and sort by week number (descending)
    return Object.entries(grouped)
      .map(([weekNumber, data]) => {
        // Get any date from this week to calculate the Sunday-Saturday range
        const anyDate = new Date(data[0].date + "T00:00:00");

        // Calculate Sunday (start of week) - go back to Sunday
        const dayOfWeek = anyDate.getDay(); // 0 = Sunday, 6 = Saturday
        const startDate = new Date(anyDate);
        startDate.setDate(anyDate.getDate() - dayOfWeek); // Go back to Sunday

        // Calculate Saturday (end of week) - Sunday + 6 days
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        // Sort data within the week by date (ascending)
        const sortedData = [...data]
          .sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          )
          .filter((row) => row.hours > 0 || row.isHoliday);
        const workedHours = sortedData
          .filter((row) => row.type === "worked")
          .reduce((sum, row) => sum + row.hours, 0);

        const approvedPTOHours = sortedData
          .filter(
            (row) =>
              row.type === "pto" &&
              (row.ptoStatus === "Approved" ||
                row.ptoStatus === "Balance_Overridden")
          )
          .reduce((sum, row) => sum + row.hours, 0);

        return {
          weekNumber: parseInt(weekNumber),
          data: sortedData,
          workedHours,
          approvedPTOHours,
          totalHours: workedHours + approvedPTOHours,
          totalFlatAmount: sortedData.reduce(
            (sum, row) => sum + (row.flatAmount || 0),
            0
          ),
          totalGiftedHours: sortedData.reduce(
            (sum, row) => sum + (row.giftedHours || 0),
            0
          ),
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
        };
      })
      .sort((a, b) => b.weekNumber - a.weekNumber);
  }, [tableData]);

  // Filter out the weekNumber column from columnDefs since we'll show it as a header
  const weekColumnDefs = useMemo(() => {
    return columnDefs.filter((col) => col.field !== "weekNumber");
  }, [columnDefs]);

  // // Calculate overall totals for holiday pay and gifted hours
  // const overallTotals = useMemo(() => {
  //   return {
  //     flatAmount: tableData.reduce(
  //       (sum, row) => sum + (row.flatAmount || 0),
  //       0
  //     ),
  //     giftedHours: tableData.reduce(
  //       (sum, row) => sum + (row.giftedHours || 0),
  //       0
  //     ),
  //   };
  // }, [tableData]);

  return (
    <div className="tw-space-y-4">
      {weeklyData.map((weekGroup) => {
        const locale = i18n.language === "es" ? "es-ES" : "en-US";
        const startDateFormatted = new Date(
          weekGroup.startDate + "T00:00:00"
        ).toLocaleDateString(locale, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const endDateFormatted = new Date(
          weekGroup.endDate + "T00:00:00"
        ).toLocaleDateString(locale, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        // Calculate height based on number of rows (header ~32px + rows ~28px each)
        const rowHeight = 28;
        const headerHeight = 32;
        const padding = 10; // Add small padding to ensure last row is fully visible
        const gridHeight =
          headerHeight + weekGroup.data.length * rowHeight + padding;

        return (
          <div
            key={weekGroup.weekNumber}
            className="tw-bg-white dark:tw-bg-[#0D1117] tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-overflow-hidden"
          >
            <div className="tw-p-4">
              <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900 dark:tw-text-gray-100">
                {t("userProfile.week")} {weekGroup.weekNumber}:{" "}
                {startDateFormatted} - {endDateFormatted}
              </h3>
              <div
                className="ag-theme-balham"
                style={{ height: gridHeight, width: "100%" }}
                onContextMenu={handleContextMenu}
              >
                <AgGridReact
                  rowData={
                    isInitialLoad && isLoading ? undefined : weekGroup.data
                  }
                  columnDefs={weekColumnDefs}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    filter: true,
                  }}
                  animateRows={true}
                  rowSelection="single"
                  getRowStyle={getRowStyle}
                  onCellContextMenu={handleCellContextMenu}
                  overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t(
                    "userProfile.loading"
                  )}</span>`}
                  overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t(
                    "userProfile.noHoursRecorded"
                  )}</span>`}
                />
              </div>
              <div className="tw-mt-4 tw-text-right tw-font-bold tw-flex tw-flex-col tw-items-end tw-gap-1">
                {/* <div>
                  {t("userProfile.total")}: {weekGroup.totalHours.toFixed(2)}{" "}
                  {t("userProfile.hrs")}
                </div> */}
                {weekGroup.workedHours > 0 && (
                  <div className="tw-text-sm tw-text-blue-600">
                    {t("userProfile.worked")}:{" "}
                    {weekGroup.workedHours.toFixed(2)} {t("userProfile.hrs")}
                  </div>
                )}
                {weekGroup.approvedPTOHours > 0 && (
                  <div className="tw-text-sm tw-text-green-600">
                    🏖️ {t("userProfile.approvedPTO")}:{" "}
                    {weekGroup.approvedPTOHours.toFixed(2)}{" "}
                    {t("userProfile.hrs")}
                  </div>
                )}
                {weekGroup.totalFlatAmount > 0 && (
                  <div className="tw-text-sm tw-text-yellow-600">
                    💰 {t("userProfile.holidayPay")}: $
                    {weekGroup.totalFlatAmount.toFixed(2)}
                  </div>
                )}
                {weekGroup.totalGiftedHours > 0 && (
                  <div className="tw-text-sm tw-text-green-600">
                    🎁 {t("userProfile.holidayHours")}:{" "}
                    {weekGroup.totalGiftedHours.toFixed(2)}{" "}
                    {t("userProfile.hrs")}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {ContextMenuComponent}

      {/* Overall total */}
      {/* <div className="tw-bg-gray-50 dark:tw-bg-gray-800 tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-4">
        <div className="tw-text-right tw-font-bold tw-text-lg tw-flex tw-flex-col tw-items-end tw-gap-1">
          <div className="tw-flex tw-items-center tw-gap-2">
            {t("userProfile.total")}: {totalHours.toFixed(2)}{" "}
            {t("userProfile.hrs")}
            {isLoading && !isInitialLoad && (
              <div className="tw-inline-block tw-w-3 tw-h-3 tw-border-2 tw-border-blue-600 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
            )}
          </div>
          {workedHours > 0 && (
            <div className="tw-text-base tw-text-blue-600">
              Worked Hours: {workedHours.toFixed(2)} hrs
            </div>
          )}
          {approvedPTOHours > 0 && (
            <div className="tw-text-base tw-text-green-600">
              🏖️ Approved PTO Hours: {approvedPTOHours.toFixed(2)} hrs
            </div>
          )}
          {overallTotals.flatAmount > 0 && (
            <div className="tw-text-base tw-text-yellow-600">
              💰 Total Holiday Pay: ${overallTotals.flatAmount.toFixed(2)}
            </div>
          )}
          {overallTotals.giftedHours > 0 && (
            <div className="tw-text-base tw-text-green-600">
              🎁 Total Holiday Hours: {overallTotals.giftedHours.toFixed(2)} hrs
            </div>
          )}
        </div>
      </div> */}
    </div>
  );
}
